import React from "react";

import styled from "styled-components";

import {
  displayName,
  formatItemValue,
} from "manager/components/vero1/Vero1ReviewPanel/formatItemValue";
import { useGetLumenV2RunById } from "manager/hooks/api/lumenQueries";
import type { DealLumenRunSummary } from "shared/interfaces/api/vero1";

import { formatRunStamp } from "../vero1Dates";

import { ANNOTATION, humanize } from "./annotation";
import { INK, MUTED } from "./styled";

/* Spec §04: Compare defaults to the two most recent entries either side.
   Client-side join on (entity, annotation) — the same #annotation: keying the
   backend's own generation parses. "Not assessed" is an honest cell (41c). */

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  font: 400 12.5px "Work Sans", sans-serif;
  color: ${INK};

  th {
    text-align: left;
    font: 600 11px "Work Sans", sans-serif;
    letter-spacing: 0.04em;
    text-transform: uppercase;
    color: ${MUTED};
    padding: 8px 10px;
    border-bottom: 1px solid #e8e7ee;
  }

  td {
    padding: 7px 10px;
    border-bottom: 1px solid #f2f2f7;
    vertical-align: top;
  }
`;

const NotAssessed = styled.span`
  color: #adaac2;
  font-style: italic;
`;

const Moved = styled.td`
  background: #fbfafe;
`;

/** The client's word for what firing this check meant — a qualifier on the
 *  check's own result, never a verdict of its own. */
const Meaning = styled.span`
  color: #6b6880;
`;

type FactMap = Map<
  string,
  { label: string; value: unknown; outcomeName?: string }
>;

/**
 * The run's configured outcome names, keyed the way history entries are
 * annotated ("#credit-avg-score-range-fail"). A check that carries one is a
 * check the client named, so the cell can say what firing it meant.
 */
const configuredNames = (run: any): Record<string, string> => {
  const outcomes = (run?.settings ?? {}).outcomes ?? {};
  const names: Record<string, string> = {};
  Object.keys(outcomes).forEach((key) => {
    const label = outcomes[key]?.scoreLabel;
    const name = displayName(label);
    if (name) names[key.replace(/^#/, "")] = name;
  });
  return names;
};

const shortVrn = (vrn: unknown): string => {
  const raw = Array.isArray(vrn) ? vrn[0] : vrn;
  if (typeof raw !== "string") return "Applicant";
  const tail = raw.split("/").pop();
  return tail ? `Applicant ${tail}` : "Applicant";
};

const entityLabel = (result: any): string =>
  result?.application ? "Deal" : shortVrn(result?.applicant);

export const runFacts = (run: any): FactMap => {
  const facts: FactMap = new Map();
  const names = configuredNames(run);
  (run?.results ?? []).forEach((result: any) => {
    const entity = entityLabel(result);
    (result?.evaluation?.history ?? []).forEach((entry: any) => {
      const match = (entry?.formula ?? "").match(ANNOTATION);
      if (!match || match[1].endsWith("-safety-net")) return;
      const key = `${entity}::${match[1]}`;
      if (!facts.has(key)) {
        facts.set(key, {
          label: `${humanize(match[1])}${
            entity === "Deal" ? "" : ` — ${entity}`
          }`,
          value: entry?.result,
          outcomeName: names[match[1]],
        });
      }
    });
  });
  return facts;
};

/**
 * This table reads Lumen's EVALUATION layer — whether each check fired — not
 * the outcome layer. A bare Pass/Fail hides what firing meant:
 * "#credit-avg-score-range-fail: true" is the applicant landing in the
 * declining band. So a check the client configured a name for says both.
 */
const cell = (fact?: { value: unknown; outcomeName?: string }) => {
  if (!fact) return <NotAssessed>Not assessed</NotAssessed>;
  if (typeof fact.value === "boolean") {
    if (!fact.outcomeName) return fact.value ? "Pass" : "Fail";
    return fact.value ? (
      <>
        Fired <Meaning>· {fact.outcomeName}</Meaning>
      </>
    ) : (
      "Not fired"
    );
  }
  if (fact.value == null) return <NotAssessed>Not assessed</NotAssessed>;
  return formatItemValue(fact.value, "");
};

const runTitle = (summary?: DealLumenRunSummary | null) =>
  summary
    ? `${summary.vero1Derived ? "VERO1 override · " : ""}${formatRunStamp(
        summary.timestamp
      )}`
    : "—";

export const RunCompare: React.FC<{
  a: DealLumenRunSummary | null;
  b: DealLumenRunSummary | null;
}> = ({ a, b }) => {
  const { lumenRun: runA } = useGetLumenV2RunById(a?.id, Boolean(a?.id));
  const { lumenRun: runB } = useGetLumenV2RunById(b?.id, Boolean(b?.id));
  const factsA = runFacts(runA);
  const factsB = runFacts(runB);
  const keys = Array.from(
    new Set(Array.from(factsA.keys()).concat(Array.from(factsB.keys())))
  ).sort();
  if (!a || !b) return null;
  return (
    <Table data-testid="run-compare">
      <thead>
        <tr>
          <th>Criterion</th>
          <th>{runTitle(b)}</th>
          <th>{runTitle(a)}</th>
        </tr>
      </thead>
      <tbody>
        {keys.map((key) => {
          const factA = factsA.get(key);
          const factB = factsB.get(key);
          const moved =
            String(factA?.value ?? "∅") !== String(factB?.value ?? "∅");
          const Cell = moved ? Moved : "td";
          return (
            <tr key={key}>
              <td>{(factA ?? factB)?.label}</td>
              <Cell>{cell(factB)}</Cell>
              <Cell>{cell(factA)}</Cell>
            </tr>
          );
        })}
      </tbody>
    </Table>
  );
};
